import { type HighlightIconVariant, type IconName } from '@dev-spendesk/grapes';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { TypeGuardError } from 'src/core/utils/switchGuard';

export type Dimension = {
  type: 'costCenter' | 'expenseCategory' | 'supplier';
};
// { type: 'customField'; customFieldId: string } // TODO

type DimensionType = Dimension['type'];

export const simpleDimensions: Dimension[] = [
  { type: 'costCenter' },
  { type: 'expenseCategory' },
  { type: 'supplier' },
];

export const BASIC_DIMENSIONS: DimensionType[] = [
  'costCenter',
  'expenseCategory',
];

export const selectAvailableDimensions = (
  dimensions: Dimension[],
  {
    hasAdvancedDimensions,
  }: {
    hasAdvancedDimensions: boolean;
  },
) =>
  hasAdvancedDimensions
    ? dimensions
    : dimensions.filter((dimension) =>
        BASIC_DIMENSIONS.includes(dimension.type),
      );

export const getDimensionTranslation = (
  dimension: Dimension,
  t: TGlobalFunctionTyped,
) => {
  switch (dimension.type) {
    case 'costCenter':
      return t('budgetByDimensions.dimensions.costCenter.title');
    case 'expenseCategory':
      return t('budgetByDimensions.dimensions.expenseCategory.title');
    case 'supplier':
      return t('budgetByDimensions.dimensions.supplier.title');
    // case 'customField':
    //   return t('budgetByDimensions.dimensions.customField.title');
    default:
      throw new TypeGuardError(dimension.type, 'Unknown dimension type');
  }
};

export const getDimensionIcon = (
  dimension: Dimension,
): { name: IconName; variant: HighlightIconVariant } => {
  switch (dimension.type) {
    case 'costCenter':
      return { name: 'building-bank', variant: 'peach' };
    case 'expenseCategory':
      return { name: 'banknote', variant: 'ocean' };
    case 'supplier':
      return { name: 'building-storefront', variant: 'lemon' };
    default:
      return { name: 'building-bank', variant: 'purple' };
  }
};

type DimensionKey = 'costCenter' | 'expenseCategory' | 'supplier';
// | `customField_${string}`; // TODO

export const getDimensionKey = (dimension: Dimension): DimensionKey => {
  switch (dimension.type) {
    case 'costCenter':
    case 'expenseCategory':
    case 'supplier':
      return dimension.type;
    // case 'customField':
    //   return `customField_${dimension.customFieldId}`;
    default:
      throw new TypeGuardError(dimension.type, 'Unknown dimension type');
  }
};

export const areDimensionsEqual = (a: Dimension, b: Dimension): boolean =>
  getDimensionKey(a) === getDimensionKey(b);

export const includesDimension = (
  dimensions: Dimension[],
  dimensionToFind: Dimension,
): boolean =>
  dimensions.some((dimension) =>
    areDimensionsEqual(dimension, dimensionToFind),
  );
